import { combineLatest, distinctUntilKeyChanged, filter, map, of } from 'rxjs';
import { whitePixel } from '@aston/foundation/functions';
import { TranslateService } from '@ngx-translate/core';
import { BusinessThemeService } from '@aston/themes';
import { inject, Injectable } from '@angular/core';

import { AI_ASSISTANT_AVATAR, AI_CURRENT_USER_AVATAR } from '../di';

import { AI_CHAT_CHARACTER_LIMIT } from './ai-chatbox.service';

@Injectable({ providedIn: 'root' })
export class AIChatboxStylesService {
	currentUser$ = inject(AI_CURRENT_USER_AVATAR)
	assistantAvatar = inject(AI_ASSISTANT_AVATAR)
    theme = inject(BusinessThemeService)
	i18n = inject(TranslateService)

	whitePixel = whitePixel()

	chatboxStyle$ = of({
		backgroundColor: 'transparent',
		borderRadius: '0',
		width: '100%',
		height: '100%',
		border: 'none',
		borderTop: '1px solid #e7eaf3',
	})

	avatarsStyle$ = this.currentUser$.pipe(
		filter(Boolean),
		distinctUntilKeyChanged('id'),
		map(user => ({
			default: {
				container: {
					'margin-top': '4px'
				}
			},
			styles: {
				avatar: {
					height: '30px',
					width: '30px'
				},
			},
			ai: {
				src: this.assistantAvatar, 
				styles: {
					avatar: {
						'margin-left': '-3px',
						height: '30px',
						width: '20px',
					}
				}
			},
			user: {
				src: user.profilePictureUrl, 
				styles: {
					avatar: {
						'border-radius': '15px'
					}
				}
			},
			system: {
				src: this.whitePixel, 
				styles: {
					avatar: {
						height: '0px',
						width: '0px'
					}
				}
			},
		}))
	)

	messageStyles$ = combineLatest([
		this.theme.getThemed('aiChat')
	]).pipe(map(([ themeStyle ]) => ({
		default: {
			shared: {
				bubble: {
					'background-color': 'transparent',
					'color': 'var(--body-color)',
					'max-width': '70%',
					...(themeStyle.messages?.shared?.bubble || {})
				}
			},
			user: {
				bubble: {
					'background-color': '#1558A6'
				}
			},
			system: {
				bubble: {
					'background-color': 'transparent',
				}
			},
			ai: {
				bubble: {
					'background-color': '#efefef',
					...(themeStyle.messages?.ai?.bubble || {})
				}
			}
		}
	})))

	errors$ = this.i18n.stream('Errors.RetryableError').pipe(
		map(text => ({
			overrides: {
				default: text,
				service: text,
				speechToText: text,
			}
		}))
	)

	textInputProps$ = combineLatest([
		this.theme.getThemed('aiChat'),
		this.i18n.stream([
			'App.AIChat.Placeholder'
		]),
	]).pipe(
		map(([themeStyle, i18n]) => ({
			characterLimit: AI_CHAT_CHARACTER_LIMIT,
			placeholder: {
				text: i18n['App.AIChat.Placeholder']
			},
			styles: {
				text: {
					color: "black",
					padding: "0.5rem"
				},
				container: {
					minHeight: "50px",
					maxHeight: "100px",
					'padding-bottom': "1rem"
				},
				focus: {
					border: "2px solid #1558A6"
				},
				...themeStyle.textInput,
			}
		}))
	)

	public combined$ = combineLatest({
		textInputProps: this.textInputProps$,
		messageStyles: this.messageStyles$,
		chatboxStyle: this.chatboxStyle$,
		avatarsStyle: this.avatarsStyle$,
		errors: this.errors$,
	})
}